import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import PaymentManager from '../../services/api/PaymentManager';
import { AppWebViewMessageTypes, PaymentMethod } from '../../services/exports/Constants';
import * as stripeJs from '@stripe/stripe-js';
import { StripeConstructorOptions, loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../store/types';
import collect from 'collect.js';
import useOrderFlow from '../../hooks/order/useOrderFlow';
import usePaymentFlow from '../../hooks/payment/usePaymentFlow';
import useMobileApp from '../../hooks/mobile-app/useMobileApp';
import WebViewResource from '../../services/resources/WebViewResource';
import useTheme from '../../hooks/ui/useTheme';

interface Props {
  children: any;
}

export interface PaymentCoordinatorContextInterface {
  loading: boolean;
  isStripePayment: boolean;
  paymentIntent: null | stripeJs.PaymentIntent;
  loadPaymentIntent: (forceCreate?: boolean) => Promise<void>;
  stripePromise: null | Promise<stripeJs.Stripe>;
  paymentsInitialized: boolean;
}

export const PaymentCoordinatorContext: React.Context<PaymentCoordinatorContextInterface> = React.createContext(null);

export default function PaymentCoordinator(props: Props): ReactElement {
  const { children } = props;

  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const theme = useTheme();
  const { data } = useOrderFlow();
  const { selectedPaymentMethod, hasOnlinePayments, isDirectPayment } = usePaymentFlow();
  const { isMobileApp } = useMobileApp();

  const [paymentIntent, setPaymentIntent] = useState(null);
  const [loading, setLoading] = useState(false);
  const isStripePayment =
    Boolean(selectedPaymentMethod) && selectedPaymentMethod !== PaymentMethod.Cash && data?.total > 0;

  const stripePromise = useMemo<null | Promise<stripeJs.Stripe>>(
    () =>
      !company?.service_provider?.stripe_key
        ? null
        : loadStripe(
            company?.service_provider?.stripe_key,
            collect({})
              .when(isDirectPayment, (options) => options.put('stripeAccount', company?.stripe_id))
              .all() as StripeConstructorOptions,
          ).then((stripe) => {
            WebViewResource.sendMessageIf(isDirectPayment && isMobileApp, AppWebViewMessageTypes.LoadStripe, {
              stripe_key: company?.service_provider.stripe_key,
              stripe_account_id: company?.stripe_id,
            });
            WebViewResource.sendMessageIf(!isDirectPayment && isMobileApp, AppWebViewMessageTypes.LoadStripe, {
              stripe_key: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY,
              stripe_account_id: null,
            });
            return stripe;
          }),
    [company?.service_provider?.stripe_key, company?.stripe_id, isDirectPayment, isMobileApp],
  );
  const loadPaymentIntent = useCallback(
    async (forceCreate: boolean = false) => {
      if (loading) {
        return;
      }

      setLoading(true);

      const body = {
        company_id: company?.id,
        payment_method_id: selectedPaymentMethod,
        force_payment_method: isMobileApp && selectedPaymentMethod === PaymentMethod.GooglePay,
      };

      const { response, success } =
        paymentIntent?.id && !forceCreate
          ? await PaymentManager.updatePaymentIntent(paymentIntent.id, body)
          : await PaymentManager.createPaymentIntent(body);

      if (success) {
        setPaymentIntent(response.data);
      }

      setTimeout(() => setLoading(false), 100);
    },
    [loading, company?.id, data.total, paymentIntent?.id, selectedPaymentMethod, isMobileApp],
  );

  useEffect(() => {
    hasOnlinePayments && stripePromise && loadPaymentIntent(true);
  }, [stripePromise, selectedPaymentMethod]);

  const paymentsInitialized = useMemo(() => {
    if (!isStripePayment) {
      return true;
    }

    return isStripePayment && stripePromise && paymentIntent;
  }, [isStripePayment, stripePromise, paymentIntent]);

  const context = useMemo(
    () => ({
      loading,
      isStripePayment,
      paymentIntent,
      stripePromise,
      paymentsInitialized,
      loadPaymentIntent,
    }),
    [loading, isStripePayment, paymentIntent, loadPaymentIntent, paymentsInitialized],
  );

  return (
    <PaymentCoordinatorContext.Provider value={context}>
      {!isStripePayment && children}
      {isStripePayment && (
        <>
          {stripePromise && paymentIntent ? (
            <Elements
              key={`company-${company?.stripe_id}-${isDirectPayment}-${paymentIntent?.client_secret}`}
              stripe={stripePromise}
              options={{
                clientSecret: paymentIntent?.client_secret,
                appearance: {
                  theme: theme.isDarkMode ? 'night' : 'stripe',
                  variables: {
                    colorPrimary: theme.color.brand.text.primary,
                    colorText: theme.color.brand.text.primary,
                  },
                },
              }}
            >
              {children}
            </Elements>
          ) : (
            children
          )}
        </>
      )}
    </PaymentCoordinatorContext.Provider>
  );
}
